// «Autoridades a las que se notifica» (CC-02, bloque B11; `ui/MG-ILE-1075.md`; RN-74).
//
// LOS DESTINOS LOS PROPONE EL SISTEMA según los registros de los productos del caso ante cada
// autoridad del país (sin registro, la predeterminada), y Asuntos Regulatorios o el
// Representante del País, en su ámbito, los AJUSTAN con motivo mientras ninguna carta esté
// preparada. Al preparar la primera se cierran: agregar o quitar responde 409 «Las cartas ya se
// empezaron a preparar: los destinos no cambian». Quitar no borra: la carta queda retirada.
//
// La pantalla no decide nada de eso: pinta el estado (`destinos.texto_del_estado`), el origen de
// cada destino (`texto_del_destino`) y los botones que el servidor ofrece
// (`destinos.acciones_permitidas`, `cartas[].acciones_del_destino`). Esas acciones NO dependen del
// rol (contrato de B6): quién puede ajustar lo decide la ruta, y su 403 se lee en el cuadro
// (CC2-20-C6). Tras cada ajuste toma el trámite que devuelve el servidor (§8.1 R3).

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Lock, MapPinned, Plus, X } from 'lucide-react';
import { Alert, Button, Card, Field, Modal, StatusBadge, Select, Textarea } from './ui';
import { MARCAS_DE_AUTORIDAD } from '../lib/etiquetas';
import { agregarDestino, quitarDestino, todasLasCartas } from '../api/cartas';
import type { CartaDelTramite, DestinosDelTramite as Destinos } from '../api/cartas';
import type { Tramite } from '../api/regulatorio';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';

/** «Escribe el motivo»: el texto del 422 del servidor (AT §11.5.4) y el de la validación local. */
export const MOTIVO_OBLIGATORIO = 'Escribe el motivo';
export const AUTORIDAD_OBLIGATORIA = 'Elige la autoridad';
const TOPE_DEL_MOTIVO = 1000;

/** Un 422 que habla del motivo va bajo su campo; los demás, en el aviso del cuadro. */
const esDelMotivo = (f: Fallo) => f.estado === 422 && /motivo/i.test(f.message);
const esDeLaAutoridad = (f: Fallo) => f.estado === 422 && /autoridad/i.test(f.message) && !/destino/i.test(f.message);

/** El aviso de un ajuste que no se hizo: sin permiso (403) o el texto de negocio del servidor. */
function FalloDelAjuste({ fallo, titulo }: Readonly<{ fallo: Fallo; titulo: string }>) {
  if (fallo.estado === 403) {
    return (
      <Alert tone="warning" title="No tienes permiso para cambiar las autoridades de este caso" detalleTecnico={fallo.detalleTecnico} data-testid="destinos-sin-permiso">
        {fallo.message}
      </Alert>
    );
  }
  return (
    <Alert tone={fallo.estado === 409 || fallo.estado === 422 ? 'warning' : 'danger'} title={titulo} detalleTecnico={fallo.detalleTecnico} data-testid="destinos-error-envio">
      {fallo.message}
    </Alert>
  );
}

/** El cuadro «Agregar una autoridad»: autoridad y motivo, los dos obligatorios. */
function AgregarAutoridad({ abierto, casoId, destinos, onCerrar, onHecho }: Readonly<{
  abierto: boolean; casoId: string; destinos: Destinos;
  onCerrar: () => void; onHecho: (t: Tramite, nombre: string) => void;
}>) {
  const [autoridad, setAutoridad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [tocados, setTocados] = useState({ autoridad: false, motivo: false });
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [errorDelServidor, setErrorDelServidor] = useState<{ autoridad?: string; motivo?: string }>({});
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (abierto) return;
    setAutoridad(''); setMotivo(''); setTocados({ autoridad: false, motivo: false });
    setFallo(null); setErrorDelServidor({});
  }, [abierto]);

  const opciones = (destinos.autoridades_para_agregar ?? []).map((a) => ({
    value: a.codigo, label: a.nombre, hint: a.por_confirmar ? MARCAS_DE_AUTORIDAD.por_confirmar : undefined,
  }));
  const errorDeAutoridad = errorDelServidor.autoridad ?? (tocados.autoridad && !autoridad ? AUTORIDAD_OBLIGATORIA : undefined);
  const errorDeMotivo = errorDelServidor.motivo ?? (tocados.motivo && !motivo.trim() ? MOTIVO_OBLIGATORIO : undefined);
  const sucio = Boolean(autoridad || motivo.trim());

  const guardar = async () => {
    setTocados({ autoridad: true, motivo: true });
    if (!autoridad || !motivo.trim()) return;
    setOcupado(true);
    setFallo(null);
    setErrorDelServidor({});
    try {
      const t = await agregarDestino(casoId, autoridad, motivo.trim());
      onHecho(t, opciones.find((o) => o.value === autoridad)?.label ?? autoridad);
    } catch (e) {
      const f = aFallo(e);
      if (esDelMotivo(f)) setErrorDelServidor({ motivo: f.message });
      else if (esDeLaAutoridad(f)) setErrorDelServidor({ autoridad: f.message });
      else setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Modal open={abierto} onClose={onCerrar} sucio={sucio} icon={Plus} data-testid="destinos-form"
      eyebrow="Notificación regulatoria · Autoridades" title="Agregar una autoridad"
      footer={<>
        <Button variant="secondary" onClick={onCerrar} disabled={ocupado} data-testid="destinos-form-cancelar">Cancelar</Button>
        <Button icon={Plus} onClick={() => { guardar(); }} disabled={ocupado} data-testid="destinos-form-guardar">
          {ocupado ? 'Agregando…' : 'Agregar autoridad'}
        </Button>
      </>}>
      <div className="space-y-4">
        {fallo && <FalloDelAjuste fallo={fallo} titulo="No se pudo agregar la autoridad" />}
        <p className="text-body text-ink-soft">Se prepara una carta más para esa autoridad, con su propio plazo.</p>
        <Field label="Autoridad" required error={errorDeAutoridad}>
          <Select options={opciones} value={autoridad} sinVacio data-testid="destinos-form-autoridad"
            onChange={(e) => { setAutoridad(String(e.target.value ?? '')); setErrorDelServidor((x) => ({ ...x, autoridad: undefined })); }}
            onBlur={() => setTocados((x) => ({ ...x, autoridad: true }))} />
        </Field>
        <Field label="Motivo" required error={errorDeMotivo} hint="Queda en la trazabilidad del caso con quién y cuándo.">
          <Textarea rows={3} maxLength={TOPE_DEL_MOTIVO} value={motivo} data-testid="destinos-form-motivo"
            onChange={(e) => { setMotivo(e.target.value); setErrorDelServidor((x) => ({ ...x, motivo: undefined })); }}
            onBlur={() => setTocados((x) => ({ ...x, motivo: true }))} />
        </Field>
      </div>
    </Modal>
  );
}

/** El cuadro «¿Quitar SANIPES…?»: el motivo es obligatorio y el error se lee bajo el campo. */
function QuitarAutoridad({ carta, casoId, onCerrar, onHecho }: Readonly<{
  carta: CartaDelTramite | null; casoId: string;
  onCerrar: () => void; onHecho: (t: Tramite, nombre: string) => void;
}>) {
  const [motivo, setMotivo] = useState('');
  const [tocado, setTocado] = useState(false);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [falloDelMotivo, setFalloDelMotivo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (carta) return;
    setMotivo(''); setTocado(false); setFallo(null); setFalloDelMotivo(null);
  }, [carta]);

  const errorDelMotivo = falloDelMotivo ?? (tocado && !motivo.trim() ? MOTIVO_OBLIGATORIO : undefined);

  const quitar = async () => {
    setTocado(true);
    if (!carta || !motivo.trim()) return;
    setOcupado(true);
    setFallo(null);
    setFalloDelMotivo(null);
    try {
      onHecho(await quitarDestino(casoId, carta.autoridad_codigo, motivo.trim()), carta.autoridad_nombre);
    } catch (e) {
      const f = aFallo(e);
      if (esDelMotivo(f)) setFalloDelMotivo(f.message);
      else setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Modal open={Boolean(carta)} onClose={onCerrar} sucio={Boolean(motivo.trim())} tone="warning" icon={X} data-testid="destinos-retiro"
      eyebrow="Notificación regulatoria · Autoridades" title={`¿Quitar ${carta?.autoridad_nombre ?? ''} de las autoridades a notificar?`}
      footer={<>
        <Button variant="secondary" onClick={onCerrar} disabled={ocupado} data-testid="destinos-retiro-cancelar">Cancelar</Button>
        <Button variant="danger" icon={X} onClick={() => { quitar(); }} disabled={ocupado} data-testid="destinos-retiro-confirmar">
          {ocupado ? 'Quitando…' : 'Quitar autoridad'}
        </Button>
      </>}>
      <div className="space-y-4">
        {fallo && <FalloDelAjuste fallo={fallo} titulo="No se pudo quitar la autoridad" />}
        <p className="text-body">
          Su carta deja de prepararse y sus plazos dejan de avisar. Puedes volver a agregarla mientras no se prepare ninguna carta.
        </p>
        <Field label="Motivo" required error={errorDelMotivo} hint="Queda en la trazabilidad del caso con quién y cuándo.">
          <Textarea rows={3} maxLength={TOPE_DEL_MOTIVO} value={motivo} data-testid="destinos-retiro-motivo"
            onChange={(e) => { setMotivo(e.target.value); setFalloDelMotivo(null); }}
            onBlur={() => setTocado(true)} />
        </Field>
      </div>
    </Modal>
  );
}

/** Una autoridad del bloque: nombre, marcas, por qué es destino y, si el servidor lo da, «Quitar». */
function FilaDelDestino({ carta, onQuitar }: Readonly<{ carta: CartaDelTramite; onQuitar: (c: CartaDelTramite) => void }>) {
  const puedeQuitarse = (carta.acciones_del_destino ?? []).includes('quitar');
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5" data-testid="destinos-autoridad" data-autoridad={carta.autoridad_codigo}>
      <span className="text-body font-semibold text-ink">{carta.autoridad_nombre}</span>
      {carta.por_confirmar && <StatusBadge tone="warning" icon={AlertTriangle}>{MARCAS_DE_AUTORIDAD.por_confirmar}</StatusBadge>}
      {!carta.activa && <StatusBadge tone="neutral" icon={X}>Quitada</StatusBadge>}
      {carta.texto_del_destino && (
        <span className="text-meta text-ink-soft min-w-0 basis-full sm:basis-auto sm:flex-1" data-testid="destinos-autoridad-origen">{carta.texto_del_destino}</span>
      )}
      {puedeQuitarse && (
        <span className="ml-auto">
          <Button size="sm" variant="ghost" icon={X} onClick={() => onQuitar(carta)} aria-label={`Quitar ${carta.autoridad_nombre}`} data-testid="destinos-quitar">
            Quitar
          </Button>
        </span>
      )}
    </li>
  );
}

/**
 * Si el bloque se muestra: con más de una carta activa, con algo que agregar o con alguna carta
 * quitada. Con una sola carta, sin nada que agregar ni quitadas, la pantalla es la de hoy (§11.6).
 *
 * «Algo que agregar» es `agregar` en `destinos.acciones_permitidas` Y alguna autoridad en
 * `autoridades_para_agregar`: el servidor da `agregar` con los destinos abiertos sin mirar el rol
 * ni si el país tiene otra autoridad, y un país con una sola autoridad no tiene nada que ajustar.
 */
export const hayAutoridadesParaAgregar = (destinos: Destinos): boolean =>
  destinos.acciones_permitidas.includes('agregar') && (destinos.autoridades_para_agregar ?? []).length > 0;

export function seMuestranLosDestinos(tramite: Tramite, destinos: Destinos | null | undefined): destinos is Destinos {
  if (!destinos) return false;
  const cartas = todasLasCartas(tramite);
  const activas = cartas.filter((c) => c.activa).length;
  return activas > 1 || hayAutoridadesParaAgregar(destinos) || cartas.some((c) => !c.activa);
}

export default function DestinosDelTramite({ casoId, tramite, destinos, onTramite }: Readonly<{
  casoId: string;
  tramite: Tramite;
  destinos: Destinos;
  /** El trámite que devolvió el servidor tras agregar o quitar (§8.1 R3). */
  onTramite: (t: Tramite) => void;
}>) {
  const [agregando, setAgregando] = useState(false);
  const [quitando, setQuitando] = useState<CartaDelTramite | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const titulo = useRef<HTMLDivElement>(null);

  const cartas = todasLasCartas(tramite);
  const puedeAgregar = hayAutoridadesParaAgregar(destinos);

  const hecho = (mensaje: string) => (t: Tramite, nombre: string) => {
    onTramite(t);
    setExito(`${mensaje}: ${nombre}.`);
    setAgregando(false);
    setQuitando(null);
    // La fila quitada puede desaparecer de la lista de acciones: el foco vuelve al bloque.
    titulo.current?.focus();
  };

  return (
    <div ref={titulo} tabIndex={-1} className="mb-4 outline-none">
      <Card title="Autoridades a las que se notifica" icon={MapPinned} data-testid="destinos-bloque"
        subtitle={destinos.texto_del_estado ?? undefined}
        actions={(
          <span className="inline-flex flex-wrap items-center gap-2">
            {destinos.cerrados && <StatusBadge tone="neutral" icon={Lock} data-testid="destinos-cerrados">Destinos cerrados</StatusBadge>}
            {puedeAgregar && (
              <Button size="sm" variant="secondary" icon={Plus} onClick={() => { setExito(null); setAgregando(true); }} data-testid="destinos-agregar">
                Agregar autoridad
              </Button>
            )}
          </span>
        )}>
        {exito && <div className="mb-3"><Alert tone="success" title={exito} onCerrar={() => setExito(null)} data-testid="destinos-exito" /></div>}
        <ul className="divide-y divide-hairline" aria-label="Autoridades a las que se notifica" data-testid="destinos-lista">
          {cartas.map((c) => <FilaDelDestino key={c.autoridad_codigo} carta={c} onQuitar={(x) => { setExito(null); setQuitando(x); }} />)}
        </ul>
      </Card>
      <AgregarAutoridad abierto={agregando} casoId={casoId} destinos={destinos} onCerrar={() => setAgregando(false)} onHecho={hecho('Destino agregado')} />
      <QuitarAutoridad carta={quitando} casoId={casoId} onCerrar={() => setQuitando(null)} onHecho={hecho('Destino quitado')} />
    </div>
  );
}
